import { EnrollmentProfilesList } from "@/components/enrollment/EnrollmentProfilesList";
import { useAdeProfiles } from "@/hooks/useAdeProfiles";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ViewToggle from "@/components/ViewToggle";
import { Badge } from "@/components/ui/badge";

export default function EnrollmentProfilesPage() {
  const { profiles: adeProfiles, loading, deleteProfile } = useAdeProfiles();
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("enrollmentProfilesViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "list";
  });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    localStorage.setItem("enrollmentProfilesViewPreference", view);
  }, [view]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background w-full overflow-hidden">
        <div className="px-6 py-6">
          <div className="text-left mb-8">
            <div className="h-8 w-48 bg-muted rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-6 space-y-4">
                <div className="h-6 w-2/3 bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-1/2 bg-muted rounded animate-pulse"></div>
                <div className="flex justify-between pt-4">
                  <div className="h-9 w-20 bg-muted rounded animate-pulse"></div>
                  <div className="h-9 w-20 bg-muted rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background w-full overflow-hidden">
      <div className="px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Enrollment Profiles</h1>
            <p className="text-muted-foreground mt-1">
              Manage Apple Business Manager enrollment profiles for automated device enrollment
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button asChild>
              <Link to="/admin/enrollment-profiles/new">
                <Plus className="mr-2 h-4 w-4" />
                New Enrollment Profile
              </Link>
            </Button>
          </div>
        </div>

        {/* Filters and Stats */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search enrollment profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-72"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{adeProfiles.length}</Badge>
                <span>Total</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="default">{adeProfiles.filter(p => p.is_mandatory).length}</Badge>
                <span>Mandatory</span>
              </div>
            </div>
            <ViewToggle view={view} onViewChange={setView} />
          </div>
        </div>

        {/* Enrollment Profiles List */}
        <EnrollmentProfilesList 
          profiles={adeProfiles} 
          view={view} 
          searchQuery={searchQuery}
          onDelete={async (id) => {
            if (confirm('Are you sure you want to delete this enrollment profile?')) {
              await deleteProfile(id);
            }
          }}
        />
      </div>
    </div>
  );
}
